import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Bell,
    CheckCheck,
    AlertTriangle,
    Megaphone,
    MessageSquare,
    UserPlus,
    Info,
    Clock,
    ShieldCheck,
    LifeBuoy,
    CalendarClock,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { useNotificationStream, type NotificationDto } from '@/hooks/useNotificationStream';
import { formatRelative } from '@/lib/formatters';

/**
 * P6-3 / M10 — notification bell with dropdown.
 *
 * Sits in the TopBar. Reads from the SSE stream (P6-2 backend) via
 * useNotificationStream. Click on a notification → mark read +
 * navigate to action_url (when set). "Mark all read" zeros the
 * badge instantly with optimistic UI.
 *
 * Type-specific icons + priority-driven badge color give the
 * operator at-a-glance triage. Time-ago format keeps the dropdown
 * scannable without forcing locale-specific date parsing on the
 * client.
 */

const ICON_BY_TYPE: Record<NotificationDto['type'], typeof Bell> = {
    broadcast: Megaphone,
    ticket_assigned: UserPlus,
    ticket_replied: MessageSquare,
    sla_warning: Clock,
    sla_breach: AlertTriangle,
    system: Info,
    agent_force_approval: ShieldCheck,
    agent_force_handoff: LifeBuoy,
    agent_force_job_result: CalendarClock,
};

const ICON_TINT_BY_PRIORITY: Record<NotificationDto['priority'], string> = {
    low: 'text-muted-foreground',
    normal: 'text-tg-info dark:text-[#A5B4FC]',
    high: 'text-tg-warning dark:text-[#FBBF24]',
    critical: 'text-tg-danger dark:text-tg-danger',
};

const DROPDOWN_LIMIT = 20;

export function NotificationBell(): JSX.Element {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { notifications, unreadCount, isConnected, markRead, markAllRead } =
        useNotificationStream();
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Click-outside / Escape closes the dropdown.
    useEffect(() => {
        if (!open) return;
        const onDoc = (ev: MouseEvent): void => {
            if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
                setOpen(false);
            }
        };
        const onKey = (ev: KeyboardEvent): void => {
            if (ev.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const visible = notifications.slice(0, DROPDOWN_LIMIT);

    const handleClickItem = async (n: NotificationDto): Promise<void> => {
        if (!n.readAt) {
            await markRead(n.id);
        }
        if (n.actionUrl) {
            setOpen(false);
            navigate(n.actionUrl);
        }
    };

    return (
        <div ref={containerRef} className="relative">
            <Button
                size="icon"
                variant="ghost"
                aria-label={t('notifications.bellAriaLabel', 'Notifications')}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="relative"
            >
                <Bell className={cn('h-4 w-4', !isConnected && 'opacity-50')} />
                {unreadCount > 0 && (
                    <span
                        className="absolute -top-0.5 -end-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-tg-danger px-1 text-[10px] font-medium text-white"
                        aria-label={t('notifications.unreadAriaLabel', { count: unreadCount, defaultValue: '{{count}} unread' })}
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </Button>

            {open && (
                <div
                    role="menu"
                    className="absolute end-0 z-50 mt-2 w-[min(360px,calc(100vw-1.5rem))] overflow-hidden rounded-md border border-tg-border bg-white text-tg-ink shadow-[var(--tg-shadow-pop)]"
                >
                    <div className="flex items-center justify-between border-b px-3 py-2">
                        <span className="text-sm font-medium">
                            {t('notifications.title', 'Notifications')}
                        </span>
                        {unreadCount > 0 && (
                            <button
                                type="button"
                                onClick={markAllRead}
                                className="inline-flex min-h-10 items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted md:min-h-0"
                            >
                                <CheckCheck className="h-3 w-3" />
                                {t('notifications.markAllRead', 'Mark all read')}
                            </button>
                        )}
                    </div>

                    {/* Phone: also clear the floating bottom tab bar (z-40, above the top bar's stacking context). */}
                    <div className="max-h-[min(420px,calc(100dvh-var(--m-tab-bar-h)-10rem))] overflow-y-auto overscroll-contain md:max-h-[min(420px,calc(100dvh-8rem))]">
                        {visible.length === 0 ? (
                            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                                {t('notifications.empty', 'You\'re all caught up.')}
                            </div>
                        ) : (
                            <ul className="divide-y">
                                {visible.map((n) => {
                                    const Icon = ICON_BY_TYPE[n.type] ?? Bell;
                                    const tint = ICON_TINT_BY_PRIORITY[n.priority];
                                    const unread = !n.readAt;
                                    return (
                                        <li key={n.id}>
                                            <button
                                                type="button"
                                                onClick={() => handleClickItem(n)}
                                                className={cn(
                                                    'flex w-full items-start gap-3 px-3 py-3 text-start hover:bg-muted/50',
                                                    unread && 'bg-muted/20',
                                                )}
                                            >
                                                <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', tint)} />
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <span className={cn('truncate text-sm', unread && 'font-medium')}>
                                                            {n.title}
                                                        </span>
                                                        {unread && (
                                                            <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-tg-info" />
                                                        )}
                                                    </div>
                                                    {n.body && (
                                                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                                                            {n.body}
                                                        </p>
                                                    )}
                                                    <p className="mt-1 text-[11px] text-muted-foreground">
                                                        {formatRelative(n.createdAt, i18n.language)}
                                                    </p>
                                                </div>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div className="border-t px-3 py-2">
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                                navigate('/notifications');
                            }}
                            className="w-full rounded px-2 py-1.5 text-center text-xs font-semibold text-tg-teal hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tg-teal/40"
                        >
                            {t('notifications.viewAll', 'View all notifications')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
